import { useEffect, useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { Search, File, Check, ChevronDown, ChevronRight, Link as LinkIcon } from "lucide-react"
import {
  BolEmailAttachment,
  BolEmailSearchResponse,
  BolEmailThreadMatch,
  DeductionDetailJson,
  link_bol_email,
  Message,
  Recipient,
  RelatedEmailThread,
  search_bol_email,
} from "@/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { LoadingSpinner } from "@/app/loading"
import { toast } from "@/components/ui/use-toast"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"

interface BolEmailSyncCardProps {
  deduction: DeductionDetailJson
}

function format_recipient(recipient: Recipient): string {
  if (recipient.name && recipient.email) return `${recipient.name} <${recipient.email}>`
  return recipient.name ?? recipient.email ?? "Unknown sender"
}

function file_url(s3_uri: string): string {
  return `/api/_s3?s3_uri=${encodeURIComponent(s3_uri)}`
}

/** "Related emails": email threads linked from outside the deduction's own conversation, plus a
 *  manual search-and-link flow for finding more. */
export function BolEmailSyncCard({ deduction }: BolEmailSyncCardProps) {
  const related = deduction.related_emails ?? []
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <div id="bol-email" class="border-t-6 border-orange-500 bg-orange-50 rounded-md p-4 space-y-3">
      <div class="flex items-center justify-between">
        <span class="flex items-center">
          <div class="w-1 bg-orange-500 mr-2 self-stretch" />
          <h3 class="text-lg font-semibold leading-5">Related emails</h3>
        </span>
        {!searchOpen && (
          <Button size="sm" variant="outline" onClick={() => setSearchOpen(true)} class="gap-1.5">
            <Search className="w-4 h-4" />
            Search email
          </Button>
        )}
      </div>

      {related.length > 0 && (
        <div class="space-y-2">
          {related.map(thread => (
            <RelatedThreadCard key={thread.thread_id} thread={thread} />
          ))}
        </div>
      )}

      {searchOpen && <BolEmailSearch deduction_id={deduction.id} onClose={() => setSearchOpen(false)} />}

      {!searchOpen && related.length === 0 && (
        <p class="text-sm text-gray-500 italic">
          No emails linked yet. Use “Search email” to find BOLs/PODs and other attachments by invoice, PO, or keyword.
        </p>
      )}
    </div>
  )
}

// ── Related (already-linked) threads — expandable to the full conversation ──────────────────────

interface RelatedThreadCardProps {
  thread: RelatedEmailThread
}

function RelatedThreadCard({ thread }: RelatedThreadCardProps) {
  const [expanded, setExpanded] = useState(false)
  const linked = new Set(thread.linked_s3_uris)
  const latest = thread.messages[thread.messages.length - 1]
  const date = latest?.date ? format(parseISO(latest.date), "MMM d, yyyy") : ""
  const snippet = latest ? plain_text(latest.body || latest.html_body).slice(0, 200) : ""

  return (
    <div class="bg-white rounded-md border border-gray-100">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        class="w-full text-left p-3 flex items-start gap-2">
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        )}
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="font-medium text-gray-800 truncate text-sm">{thread.subject || "(no subject)"}</div>
              {latest && <div class="text-gray-500 text-xs truncate">{format_recipient(latest.from)}</div>}
            </div>
            <div class="flex items-center gap-2 shrink-0">
              {thread.messages.length > 1 && (
                <span class="text-gray-400 text-xs">{thread.messages.length} messages</span>
              )}
              {date && <span class="text-gray-400 text-xs">{date}</span>}
            </div>
          </div>
          {!expanded && snippet && <p class="text-gray-500 text-xs mt-1 line-clamp-2">{snippet}</p>}
        </div>
      </button>

      {expanded ? (
        <div class="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
          {thread.messages.map(message => (
            <MessageBlock key={message.id} message={message} linked={linked} />
          ))}
        </div>
      ) : (
        <LinkedAttachments thread={thread} linked={linked} />
      )}
    </div>
  )
}

/** Compact list of the thread's linked attachments, shown when collapsed. */
function LinkedAttachments({ thread, linked }: { thread: RelatedEmailThread; linked: Set<string> }) {
  const seen = new Set<string>()
  const files = thread.messages
    .flatMap(m => m.attachments ?? [])
    .filter(a => linked.has(a.s3_uri) && !seen.has(a.s3_uri) && seen.add(a.s3_uri))
  if (files.length === 0) return null
  return (
    <div class="px-3 pb-3 -mt-1 pl-9 space-y-1">
      {files.map(att => (
        <div class="flex items-center gap-2">
          <File className="w-4 h-4 text-gray-400 shrink-0" />
          <a
            href={file_url(att.s3_uri)}
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-gray-700 underline truncate min-w-0"
            title={att.name}>
            {att.name}
          </a>
          <span class="inline-flex items-center gap-0.5 text-green-700 text-[10px] font-medium ml-auto shrink-0">
            <Check className="w-3 h-3" />
            Linked
          </span>
        </div>
      ))}
    </div>
  )
}

/** A single email message rendered with its body and attachments (used in the expanded thread). */
function MessageBlock({ message, linked }: { message: Message; linked: Set<string> }) {
  const date = message.date ? format(parseISO(message.date), "MMM d, yyyy h:mm a") : ""
  return (
    <div class="text-sm">
      <div class="flex items-center justify-between gap-2 text-gray-500 text-xs">
        <span class="truncate">{format_recipient(message.from)}</span>
        {date && <span class="shrink-0">{date}</span>}
      </div>
      <div class="mt-1 text-gray-700 break-words prose-sm" dangerouslySetInnerHTML={{ __html: message.html_body }} />
      {message.attachments && message.attachments.length > 0 && (
        <div class="mt-2 space-y-1">
          {message.attachments.map(att => (
            <div class="flex items-center gap-2">
              <File className="w-4 h-4 text-gray-400 shrink-0" />
              <a
                href={file_url(att.s3_uri)}
                target="_blank"
                rel="noopener noreferrer"
                class="text-gray-700 underline truncate min-w-0"
                title={att.name}>
                {att.name}
              </a>
              {linked.has(att.s3_uri) && (
                <span class="inline-flex items-center gap-0.5 text-green-700 text-[10px] font-medium ml-auto shrink-0">
                  <Check className="w-3 h-3" />
                  Linked
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Manual search & link ────────────────────────────────────────────────────────────────────────

interface BolEmailSearchProps {
  deduction_id: string
  onClose: () => void
}

function BolEmailSearch({ deduction_id, onClose }: BolEmailSearchProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<BolEmailSearchResponse | null>(null)
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [linking, setLinking] = useState(false)

  async function run_search(q?: string) {
    setSearching(true)
    setSelected(new Set())
    try {
      const res = await search_bol_email(deduction_id, q)
      setResults(res)
    } catch (e) {
      toast({
        title: "Search failed",
        description: e instanceof Error ? e.message : "Could not search email",
        variant: "destructive",
      })
    } finally {
      setSearching(false)
    }
  }

  // Run the default (invoice/PO) search once when the panel opens.
  useEffect(() => {
    void run_search()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function toggle(attachment_id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(attachment_id)) next.delete(attachment_id)
      else next.add(attachment_id)
      return next
    })
  }

  async function handle_link() {
    if (selected.size === 0) return
    setLinking(true)
    const res = await link_bol_email(deduction_id, [...selected])
    setLinking(false)
    if (res.ok) {
      const n = res.value.data.linked_via_row + res.value.data.linked_via_user_files
      toast({ title: "Linked", description: `Linked ${n} file${n === 1 ? "" : "s"} to this deduction.` })
      setSelected(new Set())
      triggerRefreshFlag()
      void run_search(query)
    } else {
      toast({ title: "Failed to link", description: res.error?.message ?? "Unknown error", variant: "destructive" })
    }
  }

  const matches = results?.matches ?? []
  const linkable = matches.flatMap(t => t.attachments).filter(a => !a.already_linked).length

  return (
    <div class="space-y-3 border-t border-orange-200 pt-3">
      <div class="flex items-center gap-2">
        <Input
          value={query}
          placeholder="Search by PO, invoice, or any keyword…"
          onInput={(e: Event) => setQuery((e.target as HTMLInputElement).value)}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter") void run_search(query)
          }}
          class="bg-white"
        />
        <Button size="sm" onClick={() => run_search(query)} disabled={searching}>
          Search
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      {results && results.search_terms.length > 0 && (
        <div class="flex items-center gap-1.5 flex-wrap text-xs text-gray-500">
          <span>Searching:</span>
          {results.search_terms.map(term => (
            <span key={term} class="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-medium">
              {term}
            </span>
          ))}
        </div>
      )}

      {searching && (
        <div class="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      )}

      {!searching && results && matches.length === 0 && (
        <p class="text-sm text-gray-500 italic py-2">No matching email attachments found.</p>
      )}

      {!searching && matches.length > 0 && (
        <div class="space-y-2">
          {matches.map(thread => (
            <SearchThreadCard key={thread.message_id} thread={thread} selected={selected} onToggle={toggle} />
          ))}
        </div>
      )}

      {linkable > 0 && (
        <div class="flex justify-end items-center gap-2">
          {linking ? (
            <LoadingSpinner />
          ) : (
            <Button size="sm" onClick={handle_link} disabled={selected.size === 0} class="gap-1.5">
              <LinkIcon className="w-4 h-4" />
              Link {selected.size > 0 ? `${selected.size} ` : ""}selected
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface SearchThreadCardProps {
  thread: BolEmailThreadMatch
  selected: Set<string>
  onToggle: (attachment_id: string) => void
}

function SearchThreadCard({ thread, selected, onToggle }: SearchThreadCardProps) {
  const date = thread.date ? format(parseISO(thread.date), "MMM d, yyyy") : ""

  return (
    <div class="bg-white rounded-md border border-gray-100 p-3">
      <div class="flex items-start justify-between gap-2 text-sm">
        <div class="min-w-0">
          <div class="font-medium text-gray-800 truncate">{thread.subject || "(no subject)"}</div>
          <div class="text-gray-500 text-xs truncate">{format_recipient(thread.from)}</div>
        </div>
        {date && <span class="text-gray-400 text-xs shrink-0">{date}</span>}
      </div>
      {thread.snippet && <p class="text-gray-500 text-xs mt-1 line-clamp-2">{thread.snippet}</p>}
      <div class="mt-2 space-y-1.5">
        {thread.attachments.map(att => (
          <SearchAttachmentRow
            key={att.attachment_id}
            attachment={att}
            checked={selected.has(att.attachment_id)}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  )
}

interface SearchAttachmentRowProps {
  attachment: BolEmailAttachment
  checked: boolean
  onToggle: (attachment_id: string) => void
}

function SearchAttachmentRow({ attachment, checked, onToggle }: SearchAttachmentRowProps) {
  return (
    <div class="flex items-center gap-2">
      <Checkbox
        checked={attachment.already_linked ? true : checked}
        disabled={attachment.already_linked}
        onCheckedChange={() => onToggle(attachment.attachment_id)}
      />
      <File className="w-4 h-4 text-gray-400 shrink-0" />
      <a
        href={file_url(attachment.s3_uri)}
        target="_blank"
        rel="noopener noreferrer"
        class="text-sm text-gray-700 underline truncate min-w-0"
        title={attachment.name}>
        {attachment.name}
      </a>
      <div class="flex items-center gap-1 ml-auto shrink-0">
        {attachment.matched_terms.map(term => (
          <span key={term} class="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-medium">
            {term}
          </span>
        ))}
        {attachment.already_linked && (
          <span class="inline-flex items-center gap-0.5 text-green-700 text-[10px] font-medium">
            <Check className="w-3 h-3" />
            Linked
          </span>
        )}
      </div>
    </div>
  )
}

/** Strip HTML tags and collapse whitespace for a plain-text preview. */
function plain_text(value: string | null | undefined): string {
  if (!value) return ""
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}
